import { html } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, confirmDialog, emptyRow, formValues, isOwner, ROLE_LABEL, type Page, type Role } from '../app.js';
import { ago, pill } from '../format.js';
import type { AdminUser } from '../types.js';

let users: AdminUser[] = [];
let configOwners: string[] = [];

async function load(): Promise<void> {
  const r = await get<{ users: AdminUser[]; config_owners: string[] }>('/users');
  users = r.users;
  configOwners = r.config_owners;
}

const ROLES: Role[] = ['viewer', 'admin', 'owner'];
const addedBy = (a: string) => (a === 'system:config' ? 'deployment settings' : a);

export const usersPage: Page = {
  title: 'People',
  load,
  view() {
    const owner = isOwner();
    const me = app.me.email;
    const locked = (u: AdminUser) => !owner || u.email === me || configOwners.includes(u.email);
    const add = async (e: Event) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const v = formValues(form);
      const email = (v.email ?? '').trim().toLowerCase();
      if (await act(() => api('PUT', `/users/${encodeURIComponent(email)}`, { role: v.role }), `${email} added as ${ROLE_LABEL[v.role as Role].toLowerCase()}`, load)) form.reset();
    };
    const setRole = (u: AdminUser, role: Role) => act(() => api('PUT', `/users/${encodeURIComponent(u.email)}`, { role }), `${u.email} is now ${ROLE_LABEL[role].toLowerCase()}`, load);
    const remove = async (u: AdminUser) => {
      if (await confirmDialog({ title: `Remove ${u.email}?`, body: 'They will no longer be able to open this console.', confirmLabel: 'Remove', tone: 'danger' })) {
        await act(() => api('DELETE', `/users/${encodeURIComponent(u.email)}`), 'Removed', load);
      }
    };
    return html`
      <h1>People</h1>
      <p class="hint">People sign in with company Okta; this list decides what they can do. <b>Viewers</b> see everything. <b>Admins</b> also manage API keys,
        connectors, templates and mail. <b>Owners</b> also set vendor credentials and manage this list.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Email</th><th>Role</th><th>Last seen</th><th>Added by</th>${owner ? html`<th></th>` : ''}</tr></thead>
        <tbody>${users.length
          ? users.map(
              (u) => html`<tr>
                <td>${u.email}${u.email === me ? html` ${pill('you')}` : ''}</td>
                <td>${locked(u)
                  ? pill(ROLE_LABEL[u.role], u.role === 'owner' ? 'accent' : 'neutral')
                  : html`<select class="compact" aria-label="Role for ${u.email}" @change=${(e: Event) => setRole(u, (e.target as HTMLSelectElement).value as Role)}>
                      ${ROLES.map((r) => html`<option value=${r} ?selected=${u.role === r}>${ROLE_LABEL[r]}</option>`)}</select>`}</td>
                <td>${ago(u.last_seen_at)}</td>
                <td class="muted">${addedBy(u.added_by)}</td>
                ${owner ? html`<td>${locked(u) ? '' : html`<button class="btn destructive outline compact" @click=${() => remove(u)}>Remove</button>`}</td>` : ''}
              </tr>`,
            )
          : emptyRow(5, 'Nobody yet.')}</tbody>
      </table></div>
      ${owner
        ? html`<section class="panel"><h2>Add a person</h2>
            <form class="inline" @submit=${add}>
              <input name="email" type="email" placeholder="name@catchcoverage.com" required aria-label="Email">
              <select name="role" aria-label="Role">${ROLES.map((r) => html`<option value=${r}>${ROLE_LABEL[r]}</option>`)}</select>
              <button class="btn">Add</button>
            </form>
            <p class="hint">Use the email they sign in to Okta with. They also need to be assigned to the Connectors app in Okta.</p>
          </section>`
        : ''}`;
  },
};
